import { Flex as RocketFlex } from '@rocket/ui';
import { type ComponentProps, forwardRef } from 'react';

import { type Sx, sxToCss } from './sxToCss';

type RocketFlexProps = ComponentProps<typeof RocketFlex>;
interface FlexProps extends Omit<RocketFlexProps, 'css' | 'direction'> {
  sx?: Sx;
}

// Transitional adapter: presents the rocket Flex under the legacy MUI-Box
// prop shape (`sx`) so the ~120 call sites don't have to change at once.
// `width` is pinned to match the old defaults (base/H were auto-width MUI
// Boxes; V was 100%); an explicit `sx.width` still wins. Retire by converting
// call sites to import Flex from '@rocket/ui' directly, then delete this file.
const makeFlex = (defaultWidth: string, direction?: 'row' | 'column') =>
  forwardRef<HTMLDivElement, FlexProps>(({ children, sx, ...props }, ref) => {
    const { width: sxWidth, ...css } = sxToCss(sx);
    const width = (sxWidth as string | undefined) ?? defaultWidth;
    return (
      <RocketFlex ref={ref} width={width} direction={direction} css={css} {...props}>
        {children}
      </RocketFlex>
    );
  });

const Flex = makeFlex('auto');
Flex.displayName = 'Flex';

const FlexH = makeFlex('auto', 'row');
FlexH.displayName = 'FlexH';

const FlexV = makeFlex('100%', 'column');
FlexV.displayName = 'FlexV';

const FlexNotation = Object.assign(Flex, { H: FlexH, V: FlexV });

export default FlexNotation;
